import { createSlice } from "@reduxjs/toolkit";

import { MeasuringSystem, SessionExtra, Shot, ShotExtra } from "./SessionModel";
import { getSession } from "./SessionService";
import { calculateAverage } from "./helper/CalculateAverage";
import { generateClubGroup, generateTargetGroup } from "./helper/GenerateClubGroup";
import { getTemparature } from "./helper/UnitConversions";

import { dummyClubsData, dummyTargetsData } from "../../config/ClubInfo";

const initialState: SessionExtra = {
    isLoading: false,
    status: "",
    starCount: 1,
    isNormalized: false,
    measuringSystem: MeasuringSystem.imperial,

    startTimestamp: 0,
    endTimestamp: 0,
    duration: 0,
    name: "",
    address: "",
    elevation: 0,
    temperature: 0,
    temperatureConverted: "",
    humidity: 0,
    location: "",
    normalizedElevation: 0,
    normalizedTemperature: 0,
    normalizedBallType: {},
    shots: [],
    clubs: Array(5).fill(dummyClubsData),
    targets: Array(5).fill(dummyTargetsData),
    isAnyShotSelected: false,
    durationString: "",
    drillTargets: []
}

export const sessionSlice = createSlice({
    name: "session",
    initialState,
    reducers: {
        setIsShowShots: (state, action) => {
            const payload = action.payload;
            state.clubs = state.clubs.map(club => {
                if (club.clubId === payload.clubId) {
                    club.isShowShots = payload.isShowShots
                }
                return club;
            });
            state.targets = state.targets.map(target => {
                if (target.targetId === payload.clubId) {
                    target.isShowShots = payload.isShowShots
                }
                return target;
            });
        },
        setClubIsActive: (state, action) => {
            const payload = action.payload;
            state.clubs = state.clubs.map(club => {
                if (club.clubId === payload.clubId) {
                    club.isActive = payload.isActive
                    club.isShowShots = !payload.isActive ? false : club.isShowShots
                    if (!payload.isActive) {
                        club.shots = club.shots.map(shot => {
                            shot.isSelected = false;
                            return shot;
                        })
                    }
                }
                return club;
            });
            state.isAnyShotSelected = false;
            state.clubs.forEach(club => {
                if (club.isActive) {
                    club.shots.forEach(shot => {
                        if (shot.isSelected) state.isAnyShotSelected = true;
                    })
                }
            })

        },
        setTargetIsActive: (state, action) => {
            const payload = action.payload;
            let shotsUpdate: ShotExtra[] = [];
            state.targets = state.targets.map(target => {
                if (target.targetId === payload.targetId) {
                    target.isActive = payload.isActive
                    target.isShowShots = !payload.isActive ? false : target.isShowShots
                    target.shots = target.shots.map(shot => {
                        shotsUpdate.push(shot)
                        return shot;
                    })
                }
                return target;
            });
            state.isAnyShotSelected = false;
            state.targets.forEach(target => {
                if (target.isActive) {
                    target?.shots?.forEach((shot) => {
                        if (shot.isSelected) state.isAnyShotSelected = true;
                    })
                }
            })
            state.clubs.forEach(club => {
                const findClubUpdate = shotsUpdate.find(shot => shot.clubId === club.clubId);
                if (findClubUpdate !== undefined) {
                    // Update isActive per-shot based on the toggled target, not the whole club
                    club.shots = club.shots.map(shot => {
                        if (shot.lmDrillTargetId === payload.targetId) {
                            shot.isActive = payload.isActive;
                            if (!payload.isActive) {
                                shot.isSelected = false;
                            }
                        }
                        return shot;
                    });
                    // Recalculate average after changing shot.isActive
                    club.average = calculateAverage(club.shots, state.measuringSystem);
                    // Update club active state based on shots
                    const hasActiveShots = club.shots.some(shot => shot.isActive);
                    if (payload.isActive && hasActiveShots) {
                        // When enabling target, activate club if it has active shots
                        club.isActive = true;
                    } else if (!hasActiveShots) {
                        // When disabling target, deactivate club only if no shots remain active
                        club.isActive = false;
                        club.isShowShots = false;
                    }
                }
            })
            // Recalculate isAnyShotSelected based on club shots after updates
            state.isAnyShotSelected = false;
            state.clubs.forEach(club => {
                if (club.isActive) {
                    club.shots.forEach(shot => {
                        if (shot.isSelected) state.isAnyShotSelected = true;
                    })
                }
            })

        },
        setClubShotIsActive: (state, action) => {
            const payload = action.payload;
            state.clubs = state.clubs.map(club => {
                club.shots = club.shots.map(shot => {
                    if (shot.shotIndex === payload.shotIndex) {
                        shot.isActive = payload.isActive
                        shot.isSelected = !payload.isActive ? false : payload.isSelected
                    }
                    return shot;
                })
                club.average = calculateAverage(club.shots, state.measuringSystem);
                return club;
            });
            state.targets = state.targets.map(target => {
                if (target.targetId === payload.targetId) {
                    target.shots = target.shots.map(shot => {
                        if (shot.shotIndex === payload.shotIndex) {
                            shot.isActive = payload.isActive
                            shot.isSelected = !payload.isActive ? false : payload.isSelected
                        }
                        return shot;
                    })
                    target.average = calculateAverage(target.shots, state.measuringSystem);
                }
                return target;
            });
            state.isAnyShotSelected = false;
            state.clubs.forEach(club => {
                if (club.isActive) {
                    club.shots.forEach(shot => {
                        if (shot.isSelected) state.isAnyShotSelected = true;
                    })
                }
            })
            state.targets.forEach(target => {
                if (target.isActive) {
                    target.shots.forEach(shot => {
                        if (shot.isSelected) state.isAnyShotSelected = true;
                    })
                }
            })
        },
        toggleFavoriteClub: (state, action) => {
            const payload = action.payload;
            state.clubs = state.clubs.map(club => {
                if (club.clubId === payload.clubId) {
                    club.shots = club.shots.map(shot => {
                        if (shot.shotIndex === payload.shotIndex) {
                            shot.isFavorite = payload.isFavorite
                        }
                        return shot;
                    })
                }
                return club;
            });
            state.targets = state.targets.map(target => {
                if (target.targetId === payload?.targetDistance) {
                    target.shots = target.shots.map(shot => {
                        if (shot.shotIndex === payload.shotIndex) {
                            shot.isFavorite = payload.isFavorite
                        }
                        return shot;
                    })
                }
                return target;
            });
        },
        setClubShotIsSelected: (state, action) => {
            const payload = action.payload;
            const now = Math.floor(Date.now() / 1000);
            state.clubs = state.clubs.map(club => {
                if (club.clubId === payload.clubId) {
                    club.isShowShots = true;
                    club.shots = club.shots.map(shot => {
                        if (shot.shotIndex === payload.shotIndex && shot.isActive) {
                            shot.isSelected = payload.isSelected
                            shot.selectedTimestamp = payload.isSelected ? now : 0;
                        }
                        return shot
                    })
                    club.selectedTimestamp = now;
                }
                return club;
            });

            state.targets = state.targets.map(target => {
                const hasShot = target.shots.some(shot => shot.shotIndex === payload.shotIndex && shot.isActive);
                if (hasShot) {
                    if (payload.isSelected) target.isShowShots = true;
                    target.shots = target.shots.map(shot => {
                        if (shot.shotIndex === payload.shotIndex && shot.isActive) {
                            shot.isSelected = payload.isSelected;
                            shot.selectedTimestamp = payload.isSelected ? now : 0;
                        }
                        return shot;
                    });
                }
                return target;
            });

            state.isAnyShotSelected = false;
            state.clubs.forEach(club => {
                if (club.isActive) {
                    club.shots.forEach(shot => {
                        if (shot.isSelected) state.isAnyShotSelected = true;
                    })
                }
            })
            state.targets.forEach(target => {
                if (target.isActive) {
                    target.shots.forEach(shot => {
                        if (shot.isSelected) state.isAnyShotSelected = true;
                    })
                }
            })
        },
        setIsNormalized: (state, action) => {
            const payload = action.payload;
            state.isNormalized = payload.isNormalized;
            state.temperatureConverted = getTemparature(state.normalizedTemperature, state.measuringSystem)
            state.clubs = generateClubGroup(state.shots, state.isNormalized, state.measuringSystem, [], state.location);
            state.targets = generateTargetGroup(state.shots, state.isNormalized, state.measuringSystem, [], state.drillTargets, state.location);
            state.isAnyShotSelected = false;
            state.clubs.forEach(club => {
                if (club.isActive) {
                    club.shots.forEach(shot => {
                        if (shot.isSelected) state.isAnyShotSelected = true;
                    })
                }
            })
            state.targets.forEach(target => {
                if (target.isActive) {
                    target.shots.forEach(shot => {
                        if (shot.isSelected) state.isAnyShotSelected = true;
                    })
                }
            })
        },
        setMeasuringSystem: (state, action) => {
            const payload = action.payload;
            state.measuringSystem = payload.measuringSystem;
            state.temperatureConverted = getTemparature(state.normalizedTemperature, state.measuringSystem)
            state.clubs = generateClubGroup(state.shots, state.isNormalized, state.measuringSystem, state.clubs, state.location);
            state.targets = generateTargetGroup(state.shots, state.isNormalized, state.measuringSystem, state.targets, state.drillTargets, state.location);
            state.isAnyShotSelected = false;
            state.clubs.forEach(club => {
                if (club.isActive) {
                    club.shots.forEach(shot => {
                        if (shot.isSelected) state.isAnyShotSelected = true;
                    })
                }
            })
            state.targets.forEach(target => {
                if (target.isActive) {
                    target.shots.forEach(shot => {
                        if (shot.isSelected) state.isAnyShotSelected = true;
                    })
                }
            })
        }
    },
    extraReducers: (builder) => {
        builder.addCase(getSession.pending.type, (state, action) => {
            state.status = "pending";
            state.isLoading = true;
        });
        builder.addCase(getSession.fulfilled.type, (state, action: any) => {
            if (action.payload) {
                state = {
                    status: "success",
                    isLoading: false,
                    starCount: action.payload?.shots?.filter((shot: Shot) => shot.isFavorite).length || 0,
                    measuringSystem: MeasuringSystem.imperial,
                    isNormalized: false,
                    isAnyShotSelected: false,
                    ...action.payload
                }
                return state;
            } else {
                state.status = "failed";
                state.isLoading = false;
            }
        });
        builder.addCase(getSession.rejected.type, (state, payload) => {
            state.status = "failed";
            state.isLoading = false;
        });
    }
})

export default sessionSlice
